import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { invitationAPI } from '../lib/api';
import { firebaseRegister } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, Phone, CheckCircle, XCircle } from 'lucide-react';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await invitationAPI.verify(token);
      setInviteData(res.data);
      setFormData((prev) => ({
        ...prev,
        name: res.data.name || res.data.invitation?.name || '',
        email: res.data.email || res.data.invitation?.email || '',
      }));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 410 || detail.includes('expired')) {
        setError('This invitation has expired. Please contact your landlord for a new one.');
      } else if (detail.includes('accepted')) {
        setError('This invitation has already been used. Please login with your account.');
      } else if (detail.includes('revoked')) {
        setError('This invitation has been cancelled. Please contact your landlord.');
      } else {
        setError(detail || 'Invalid invitation. The link may be invalid or revoked.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      // Create Firebase account
      const fbUser = await firebaseRegister(formData.email, formData.password, formData.name);

      // Accept invitation on backend
      await invitationAPI.accept({
        token,
        firebase_uid: fbUser.uid,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
      });

      setSuccess(true);
      toast.success('Account created! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const code = err?.code || '';
      const msg = code.includes('email-already-in-use')
        ? 'An account with this email already exists. Please sign in instead.'
        : code.includes('weak-password')
        ? 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.'
        : err.response?.data?.detail || err.message || 'Failed to accept invitation';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-gold text-lg">Verifying invitation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="invite-error">
        <div className="max-w-md w-full text-center space-y-6 p-8">
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="font-serif text-2xl">Invitation Invalid</h2>
          <p className="text-muted-foreground">{error}</p>
          <Button asChild className="bg-gold text-white hover:bg-gold-light">
            <Link to="/login">Go to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="invite-success">
        <div className="max-w-md w-full text-center space-y-6 p-8">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
          <h2 className="font-serif text-2xl">Welcome to Realty JAG!</h2>
          <p className="text-muted-foreground">Your account has been created. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="accept-invite-page">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1727672951734-2406581ebb39?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="font-serif text-5xl text-white mb-4">
            Realty <span className="text-gold">JAG</span>
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            You've been invited to join {inviteData?.organisation_name}. Set up your account to get started.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md animate-fadeIn py-8">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <Building2 className="w-8 h-8 text-gold" />
            <span className="font-serif text-2xl">
              Realty <span className="text-gold">JAG</span>
            </span>
          </div>

          <div className="mb-8">
            <h2 className="font-serif text-3xl mb-2">Accept Invitation</h2>
            <p className="text-muted-foreground">
              Join <span className="text-gold font-medium">{inviteData?.organisation_name}</span> as a tenant
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-12 h-12 bg-white border-[#e5e7eb] focus:border-gold"
                  placeholder="Your full name"
                  required
                  data-testid="invite-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  className="pl-12 h-12 bg-white border-[#e5e7eb] opacity-70 cursor-not-allowed"
                  readOnly
                  data-testid="invite-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">Phone (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-12 h-12 bg-white border-[#e5e7eb] focus:border-gold"
                  placeholder="+1 (555) 000-0000"
                  data-testid="invite-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-12 h-12 bg-white border-[#e5e7eb] focus:border-gold"
                  placeholder="Create a password"
                  required
                  data-testid="invite-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-12 h-12 bg-white border-[#e5e7eb] focus:border-gold"
                  placeholder="Confirm password"
                  required
                  data-testid="invite-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-gold text-white hover:bg-gold-light font-semibold uppercase tracking-wider"
              data-testid="invite-submit-btn"
            >
              {submitting ? 'Creating Account...' : 'Accept & Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-gold hover:text-gold-light transition-colors">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
